<script setup>
import { computed, ref, watch } from 'vue';
import ContactPanel from 'dashboard/routes/dashboard/conversation/ContactPanel.vue';
import DashboardAppFrame from 'dashboard/components/widgets/DashboardApp/Frame.vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  currentChat: {
    required: true,
    type: Object,
  },
});

const { uiSettings, updateUISettings } = useUISettings();
const { width: windowWidth } = useWindowSize();
const dashboardApps = useMapGetter('dashboardApps/getRecords');
const activePanel = ref('business');

const businessDashboardApp = computed(() =>
  dashboardApps.value.find(app => app.title === '客户业务中心')
);

const showBusinessPanel = computed(
  () => activePanel.value === 'business' && !!businessDashboardApp.value
);

watch(
  () => props.currentChat.id,
  () => {
    activePanel.value = 'business';
  }
);

const activeTab = computed(() => {
  const { is_contact_sidebar_open: isContactSidebarOpen } = uiSettings.value;

  if (isContactSidebarOpen) {
    return 0;
  }
  return null;
});

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const closeContactPanel = () => {
  if (isSmallScreen.value && uiSettings.value?.is_contact_sidebar_open) {
    updateUISettings({
      is_contact_sidebar_open: false,
      is_copilot_panel_open: false,
    });
  }
};
</script>

<template>
  <div
    v-on-click-outside="[
      () => closeContactPanel(),
      {
        ignore: [
          'dialog.ProseMirror-prompt-backdrop',
          '[data-popover-content]',
          '[data-popover-backdrop]',
        ],
      },
    ]"
    class="bg-n-surface-2 h-full overflow-hidden flex flex-col fixed top-0 z-40 w-full max-w-sm transition-transform duration-300 ease-in-out ltr:right-0 rtl:left-0 md:static md:w-[320px] md:min-w-[320px] ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[360px] 2xl:w-[360px] shadow-lg md:shadow-none"
    :class="[
      {
        'md:flex': activeTab === 0,
        'md:hidden': activeTab !== 0,
      },
    ]"
  >
    <div v-if="activeTab === 0" class="flex flex-1 min-h-0 flex-col">
      <div
        v-if="businessDashboardApp"
        class="flex items-center gap-1 px-2 py-2 border-b border-n-weak bg-n-surface-2"
      >
        <button
          type="button"
          class="flex-1 h-8 rounded-md text-sm font-medium transition-colors"
          :class="
            activePanel === 'contact'
              ? 'bg-n-alpha-2 text-n-slate-12'
              : 'text-n-slate-11 hover:bg-n-alpha-1'
          "
          @click="activePanel = 'contact'"
        >
          客户资料
        </button>
        <button
          type="button"
          class="flex-1 h-8 rounded-md text-sm font-medium transition-colors"
          :class="
            showBusinessPanel
              ? 'bg-n-alpha-2 text-n-slate-12'
              : 'text-n-slate-11 hover:bg-n-alpha-1'
          "
          @click="activePanel = 'business'"
        >
          业务中心
        </button>
        <button
          v-if="showBusinessPanel"
          v-tooltip.left="'关闭侧边栏'"
          type="button"
          aria-label="关闭侧边栏"
          class="size-8 shrink-0 rounded-md text-n-slate-11 hover:bg-n-alpha-1"
          @click="closeContactPanel"
        >
          <span class="i-ph-x text-lg" />
        </button>
      </div>
      <div v-show="!showBusinessPanel" class="flex-1 min-h-0 overflow-auto">
        <ContactPanel
          :conversation-id="currentChat.id"
          :inbox-id="currentChat.inbox_id"
        />
      </div>
      <div
        v-if="businessDashboardApp"
        v-show="showBusinessPanel"
        class="flex-1 min-h-0"
      >
        <DashboardAppFrame
          :key="`${currentChat.id}-${businessDashboardApp.id}-sidebar`"
          :is-visible="showBusinessPanel"
          :config="businessDashboardApp.content"
          :position="9000"
          :current-chat="currentChat"
        />
      </div>
    </div>
  </div>
</template>
